/** Transport-level failures (network down, DNS, connection refused) never reach the server, so they carry no HTTP status. */
const TRANSPORT_FAILURE_STATUS = 0;

/**
 * - `"NetworkError"` — the request never reached the server (no response).
 * - `"ServerError"` — the server responded with a non-2xx status.
 */
export type ApiErrorType = "NetworkError" | "ServerError";

/**
 * The single error thrown by every API hook. Plain fields only — read them
 * directly, no getters or factories. `i18nKey` is an `@noa/i18n` `common`-namespace
 * key, so any consumer renders the user-facing message with `t(error.i18nKey)`.
 */
export class ApiError extends Error {
  readonly type: ApiErrorType;
  readonly status: number;
  readonly i18nKey: string;
  /** The `detail` of the response body (FastAPI errors), for callers that act on a specific code. */
  readonly detail?: unknown;

  constructor(type: ApiErrorType, status: number, i18nKey: string, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.type = type;
    this.status = status;
    this.i18nKey = i18nKey;
    this.detail = detail;
  }
}

/** Generic, global status→key mapping — a 401 is always "session expired", etc. */
const i18nKeyForStatus = (status: number): string => {
  switch (status) {
    case 401:
      return "common:sessionExpired";
    case 403:
      return "common:errorForbidden";
    case 404:
      return "common:errorNotFound";
    default:
      return status >= 500 ? "common:errorServer" : "common:errorUnexpected";
  }
};

/** The `detail` key of a parsed error body, if it is a JSON object carrying one. */
const detailOf = (body: unknown): unknown =>
  typeof body === "object" && body !== null && "detail" in body ? body.detail : undefined;

/** Builds the {@link ApiError} for a non-2xx HTTP response, keeping the body's `detail`. */
export const serverError = (status: number, statusText = "", body?: unknown): ApiError =>
  new ApiError(
    "ServerError",
    status,
    i18nKeyForStatus(status),
    `Request failed with status ${status}${statusText ? ` (${statusText})` : ""}`,
    detailOf(body)
  );

/** Builds the {@link ApiError} for a transport-level failure (no response received). */
export const networkError = (): ApiError =>
  new ApiError("NetworkError", TRANSPORT_FAILURE_STATUS, "common:errorNetwork", "Network request failed");

/**
 * Reads the i18n key from any thrown value. Use at untyped call sites — error boundaries —
 * where the value is `unknown`. When you already hold an {@link ApiError}, read `error.i18nKey` directly.
 */
export const getApiErrorI18nKey = (error: unknown): string =>
  error instanceof ApiError ? error.i18nKey : "common:errorUnexpected";

/**
 * Normalises any thrown value to an {@link ApiError}. Pass-through when already an ApiError;
 * wraps anything else as an unexpected error so `onError` always receives a typed value.
 */
export const toApiError = (error: unknown): ApiError =>
  error instanceof ApiError
    ? error
    : new ApiError("ServerError", 0, "common:errorUnexpected", error instanceof Error ? error.message : String(error));
